import { apiFetch } from './client';

// Billing settings and Stripe Connect (Express). Owner / administrator only;
// the SPA hides the Billing tab for other roles.

export interface BillingSettings {
  currency: string; // '' when unlocked
  currency_locked: boolean;
  fiscal_year_start_month: number;
  vat_default_rate_bp: number; // basis points (e.g. 2500 = 25%)
  default_due_days: number;
  invoice_email_from: string;
  invoice_email_reply_to: string;
  invoice_footer_text: string;
  reminder_offset_days_first: number | null;
  reminder_offset_days_second: number | null;
  reminder_offset_days_overdue: number | null;
  reminder_send_email: boolean;
  reminder_send_push: boolean;
  // Invoice header block (legal compliance + branding).
  sender_address_line1: string;
  sender_address_line2: string;
  sender_postal_code: string;
  sender_city: string;
  sender_country: string;
  organization_number: string;
  vat_number: string;
  bankgiro: string;
  plusgiro: string;
  iban: string;
  bic: string;
  f_skatt: boolean;
  logo_object_key: string;
  // Family-billing config (D4). When enabled, the family-invoice flow bills a
  // guardian's dependents on one invoice with the chosen discount model applied.
  family_billing_enabled: boolean;
  family_discount_model: FamilyDiscountModel;
  family_flat_fee_minor: number | null;
  family_percentage_bp: number | null;
  family_tier_discounts_bp: number[];
  family_cap_minor: number | null;
  family_tier_order: FamilyTierOrder;
  family_discount_invoice_type_id: string; // '' = all types (usually membership)
}

export type FamilyDiscountModel = 'none' | 'sibling_tiered' | 'flat_fee' | 'percentage';
export type FamilyTierOrder = 'fee_desc' | 'age_asc' | 'age_desc';

export type BillingSettingsUpdate = Omit<BillingSettings, 'currency' | 'currency_locked'>;

export async function getBillingSettings(token: string | null, slug: string): Promise<BillingSettings> {
  return apiFetch<BillingSettings>(`/api/v1/organizations/${encodeURIComponent(slug)}/billing/settings`, {
    token,
  });
}

export async function updateBillingSettings(
  token: string | null,
  slug: string,
  patch: BillingSettingsUpdate,
): Promise<BillingSettings> {
  return apiFetch<BillingSettings>(`/api/v1/organizations/${encodeURIComponent(slug)}/billing/settings`, {
    method: 'PUT',
    body: patch,
    token,
  });
}

// Stripe Connect (Express). `connected: false` when no row exists yet.

export interface StripeAccountStatus {
  connected: boolean;
  stripe_account_id?: string;
  account_type?: string;
  country?: string;
  default_currency?: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  details_submitted: boolean;
  requirements_due: string[];
  disabled_reason?: string;
  onboarded_at?: string;
  last_synced_at?: string;
}

export async function getStripeAccount(token: string | null, slug: string): Promise<StripeAccountStatus> {
  return apiFetch<StripeAccountStatus>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/stripe/account`,
    { token },
  );
}

// Org-wide invoice aggregate behind the Fakturor dashboard charts. Money fields
// are minor units of `currency`; counts cover every non-deleted invoice.
export interface InvoiceSummary {
  currency: string;
  count_draft: number;
  count_issued: number;
  count_partially_paid: number;
  count_paid: number;
  count_overdue: number;
  count_void: number;
  collected_minor: number;
  outstanding_minor: number;
  expected_minor: number;
  refunded_minor: number;
}

export async function getInvoiceSummary(token: string | null, slug: string): Promise<InvoiceSummary> {
  return apiFetch<InvoiceSummary>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/summary`,
    { token },
  );
}

// Cash-flow lookahead for the home dashboard's "money coming in" tile:
// still-owed money due within the next `days`, plus what's already overdue.
export interface InvoiceForecast {
  currency: string;
  from: string; // RFC3339
  to: string; // RFC3339
  incoming_minor: number; // owed, due inside the window
  overdue_minor: number; // owed, already past due
  count: number; // # collectible invoices due in the window
}

export async function getInvoiceForecast(
  token: string | null,
  slug: string,
  days = 30,
): Promise<InvoiceForecast> {
  return apiFetch<InvoiceForecast>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/forecast?days=${days}`,
    { token },
  );
}

// InvoiceMonth / InvoiceHistory mirror internal/services/billing's structs.
// Bucketed by DUE date, same basis as the forecast tile — billed is what was
// asked for, collected is what arrived, and the gap is the story.
export interface InvoiceMonth {
  month: string; // RFC3339, first instant of the month (UTC)
  billed_minor: number;
  collected_minor: number;
  outstanding_minor: number;
  count: number;
}

export interface InvoiceHistory {
  currency: string;
  months: InvoiceMonth[];
}

export async function getInvoiceHistory(
  token: string | null,
  slug: string,
  months = 12,
): Promise<InvoiceHistory> {
  return apiFetch<InvoiceHistory>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/history?months=${months}`,
    { token },
  );
}

// MembershipYear / MembershipHistory mirror billing.MembershipService's
// PaidMembershipHistory. `configured: false` means the club has no invoice type
// flagged as the membership fee, so every paid count is 0 for a reason that has
// nothing to do with its members — callers must fall back to the roster count
// instead of rendering a misleading zero.
export interface MembershipYear {
  fiscal_year: number;
  paid_members: number;
  billed_members: number;
}

export interface MembershipHistory {
  configured: boolean;
  current_fiscal_year: number;
  years: MembershipYear[];
}

export async function getMembershipHistory(
  token: string | null,
  slug: string,
  years = 5,
): Promise<MembershipHistory> {
  return apiFetch<MembershipHistory>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/membership/history?years=${years}`,
    { token },
  );
}

export interface StartOnboardingInput {
  country: string;
  email: string;
  refresh_url: string;
  return_url: string;
}

export async function startStripeOnboarding(
  token: string | null,
  slug: string,
  input: StartOnboardingInput,
): Promise<{ url: string }> {
  return apiFetch<{ url: string }>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/stripe/onboarding`,
    { method: 'POST', body: input, token },
  );
}

export async function refreshStripeAccount(token: string | null, slug: string): Promise<StripeAccountStatus> {
  return apiFetch<StripeAccountStatus>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/stripe/refresh`,
    { method: 'POST', token },
  );
}

export async function disconnectStripeAccount(token: string | null, slug: string): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/stripe/disconnect`,
    { method: 'POST', token },
  );
}

// Invoices.

export interface InvoiceLine {
  id?: string;
  ord: number;
  description: string;
  quantity_milli: number;
  unit_amount_minor: number;
  vat_rate_bp: number;
  line_total_minor?: number;
  line_vat_minor?: number;
}

export interface Invoice {
  id: string;
  // Groups invoices created in a single CreateBatch call. Single-recipient
  // creates get a fresh batch_id, so every invoice belongs to exactly one
  // batch (a batch of 1 is still a batch).
  batch_id: string;
  number?: string;
  fiscal_year?: number;
  ocr_reference?: string;
  status: 'draft' | 'issued' | 'partially_paid' | 'paid' | 'overdue' | 'void';
  currency?: string;
  invoice_type_id?: string;
  member_id?: string;
  customer_name: string;
  customer_email?: string;
  customer_address_line1?: string;
  customer_address_line2?: string;
  customer_postal_code?: string;
  customer_city?: string;
  customer_country?: string;
  issue_date?: string; // YYYY-MM-DD
  due_date?: string;
  subtotal_minor: number;
  vat_minor: number;
  total_minor: number;
  amount_paid_minor: number;
  amount_refunded_minor: number;
  notes?: string;
  stripe_payment_intent_id?: string;
  public_token?: string;
  pdf_object_key?: string;
  issued_at?: string;
  voided_at?: string;
  created_at: string;
  lines?: InvoiceLine[];
}

export interface ListInvoicesResponse {
  invoices: Invoice[];
  total: number;
}

export interface ListInvoicesOpts {
  limit?: number;
  offset?: number;
  status?: string;
  member_id?: string;
  // When set, restricts the list to invoices in a single batch (drill-in
  // view from the batch-grouped list).
  batch_id?: string;
  // ILIKE on customer_name / customer_email / number — used by the search
  // input on the flat list view.
  search?: string;
  // Server-side sort. Allowed values: 'created' | 'number' | 'customer'
  // | 'issued' | 'total' | 'status'. Unknown values fall back to created.
  sort?: string;
  order?: 'asc' | 'desc';
}

export async function listInvoices(
  token: string | null,
  slug: string,
  opts: ListInvoicesOpts = {},
): Promise<ListInvoicesResponse> {
  const qs = new URLSearchParams();
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
  if (opts.status) qs.set('status', opts.status);
  if (opts.member_id) qs.set('member_id', opts.member_id);
  if (opts.batch_id) qs.set('batch_id', opts.batch_id);
  if (opts.search) qs.set('search', opts.search);
  if (opts.sort) qs.set('sort', opts.sort);
  if (opts.order) qs.set('order', opts.order);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<ListInvoicesResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices${suffix}`,
    { token },
  );
}

// Batch-grouped list. Returns one row per batch_id (across all
// non-deleted invoices in the org), with the count, number range, total
// and a "summary status" that collapses to 'mixed' when the constituent
// invoices disagree.
export interface BatchSummary {
  batch_id: string;
  invoice_count: number;
  number_first?: string;
  number_last?: string;
  summary_status:
    | 'draft'
    | 'issued'
    | 'partially_paid'
    | 'paid'
    | 'overdue'
    | 'void'
    | 'mixed';
  total_minor: number;
  currency?: string;
  issue_date?: string;
  created_at: string;
}

export interface ListBatchesResponse {
  batches: BatchSummary[];
  total: number;
}

export interface ListBatchesOpts {
  limit?: number;
  offset?: number;
  status?: string; // matches the summary status
  search?: string;
  sort?: string; // 'created' | 'issued' | 'total' | 'count' | 'status'
  order?: 'asc' | 'desc';
}

export async function listInvoiceBatches(
  token: string | null,
  slug: string,
  opts: ListBatchesOpts = {},
): Promise<ListBatchesResponse> {
  const qs = new URLSearchParams();
  if (opts.limit !== undefined) qs.set('limit', String(opts.limit));
  if (opts.offset !== undefined) qs.set('offset', String(opts.offset));
  if (opts.status) qs.set('status', opts.status);
  if (opts.search) qs.set('search', opts.search);
  if (opts.sort) qs.set('sort', opts.sort);
  if (opts.order) qs.set('order', opts.order);
  const suffix = qs.toString() ? `?${qs.toString()}` : '';
  return apiFetch<ListBatchesResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/batches${suffix}`,
    { token },
  );
}

// Batch creation — fans out one invoice per recipient with a shared
// batch_id. issue_now=true also allocates numbers + OCR per invoice in
// the same transaction. Single-recipient invoices also use this path
// so the SPA has one create entry point.
export interface BatchRecipientInput {
  member_id?: string;
  customer_name: string;
  customer_email?: string;
  customer_address_line1?: string;
  customer_address_line2?: string;
  customer_postal_code?: string;
  customer_city?: string;
  customer_country?: string;
}

export interface CreateInvoiceBatchInput {
  recipients: BatchRecipientInput[];
  lines: InvoiceLine[];
  notes?: string;
  issue_now?: boolean;
  currency?: string;
  issue_date?: string; // YYYY-MM-DD when issue_now
  due_date?: string;
}

export interface CreateInvoiceBatchResult {
  batch_id: string;
  invoice_ids: string[];
  issued: boolean;
  number_range?: string;
}

export async function createInvoiceBatch(
  token: string | null,
  slug: string,
  input: CreateInvoiceBatchInput,
): Promise<CreateInvoiceBatchResult> {
  return apiFetch<CreateInvoiceBatchResult>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/batch`,
    { method: 'POST', body: input, token },
  );
}

// Family invoices (D4). Bill a guardian's dependents on one invoice with the
// org's configured discount model applied server-side.
export interface FamilyDraftMember {
  member_id: string;
  base_fee_minor: number;
  vat_rate_bp?: number;
}

export interface CreateFamilyInvoiceInput {
  guardian_member_id: string;
  invoice_type_id?: string;
  notes?: string;
  members: FamilyDraftMember[];
}

export interface FamilyPreviewLine {
  subject_member_id?: string;
  member_name?: string;
  kind: 'fee' | 'discount';
  reason?: string;
  amount_minor: number;
  vat_rate_bp: number;
}

export interface FamilyPreview {
  lines: FamilyPreviewLine[];
  subtotal_minor: number;
  vat_minor: number;
  total_minor: number;
}

export async function previewFamilyInvoice(
  token: string | null,
  slug: string,
  input: CreateFamilyInvoiceInput,
): Promise<FamilyPreview> {
  return apiFetch<FamilyPreview>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/family/preview`,
    { method: 'POST', body: input, token },
  );
}

export async function createFamilyInvoice(
  token: string | null,
  slug: string,
  input: CreateFamilyInvoiceInput,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/family`,
    { method: 'POST', body: input, token },
  );
}

export async function getInvoice(token: string | null, slug: string, id: string): Promise<Invoice> {
  return apiFetch<Invoice>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/${encodeURIComponent(id)}`,
    { token },
  );
}

export interface CreateInvoiceInput {
  member_id?: string;
  invoice_type_id?: string;
  customer_name: string;
  customer_email?: string;
  customer_address_line1?: string;
  customer_address_line2?: string;
  customer_postal_code?: string;
  customer_city?: string;
  customer_country?: string;
  notes?: string;
  lines?: InvoiceLine[];
}

export async function createInvoice(
  token: string | null,
  slug: string,
  input: CreateInvoiceInput,
): Promise<{ id: string }> {
  return apiFetch<{ id: string }>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices`,
    { method: 'POST', body: input, token },
  );
}

export async function updateInvoiceLines(
  token: string | null,
  slug: string,
  id: string,
  lines: InvoiceLine[],
): Promise<Invoice> {
  return apiFetch<Invoice>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/${encodeURIComponent(id)}/lines`,
    { method: 'PUT', body: { lines }, token },
  );
}

// Editable subset of an invoice draft's header. Sent in full (PUT semantics):
// blank strings clear the field, omitting member_id clears the link.
export interface UpdateInvoiceRecipientInput {
  member_id?: string;
  invoice_type_id?: string;
  customer_name: string;
  customer_email?: string;
  customer_address_line1?: string;
  customer_address_line2?: string;
  customer_postal_code?: string;
  customer_city?: string;
  customer_country?: string;
}

export async function updateInvoiceRecipient(
  token: string | null,
  slug: string,
  id: string,
  input: UpdateInvoiceRecipientInput,
): Promise<Invoice> {
  return apiFetch<Invoice>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/${encodeURIComponent(id)}/recipient`,
    { method: 'PUT', body: input, token },
  );
}

export interface IssueInvoiceInput {
  currency: string;
  issue_date: string;
  due_date: string;
}

export async function issueInvoice(
  token: string | null,
  slug: string,
  id: string,
  input: IssueInvoiceInput,
): Promise<Invoice> {
  return apiFetch<Invoice>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/${encodeURIComponent(id)}/issue`,
    { method: 'POST', body: input, token },
  );
}

export async function voidInvoice(
  token: string | null,
  slug: string,
  id: string,
  reason: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/${encodeURIComponent(id)}/void`,
    { method: 'POST', body: { reason }, token },
  );
}

// Refunds (D-9). amount_minor = 0 means "full refund of remaining
// captured-minus-already-refunded". reason is one of 'duplicate',
// 'fraudulent', 'requested_by_customer' or empty (most common).
export interface RefundInput {
  amount_minor?: number;
  reason?: string;
}

export interface RefundResult {
  id: string;
  status: string;
  amount_minor: number;
  currency: string;
  stripe_refund_id: string;
}

export async function refundInvoice(
  token: string | null,
  slug: string,
  invoiceID: string,
  input: RefundInput = {},
): Promise<RefundResult> {
  return apiFetch<RefundResult>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/${encodeURIComponent(invoiceID)}/refunds`,
    { method: 'POST', body: { amount_minor: input.amount_minor ?? 0, reason: input.reason ?? '' }, token },
  );
}

// Record an offline / manual payment (bankgiro, plusgiro, Swish, cash) against
// an issued invoice — for clubs that take money outside Stripe. Converges on the
// same status path Stripe payments use, so the invoice (and PR1 membership
// status) update identically. Returns the refreshed invoice.
export type ManualPaymentMethod = 'bank_transfer' | 'swish' | 'cash' | 'other';

export interface RecordManualPaymentInput {
  amount_minor: number;
  method: ManualPaymentMethod;
  currency?: string;
  reference?: string;
  received_date?: string; // YYYY-MM-DD; defaults to today
}

export async function recordManualPayment(
  token: string | null,
  slug: string,
  invoiceID: string,
  input: RecordManualPaymentInput,
): Promise<Invoice> {
  return apiFetch<Invoice>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/${encodeURIComponent(invoiceID)}/payments`,
    { method: 'POST', body: input, token },
  );
}

// Returns the URL the SPA should navigate to for downloading the PDF.
// Bypasses apiFetch because the response is binary, not JSON. The browser
// will follow the URL with the auth cookie / fetch headers handled by the
// caller (we open it via window.open with a Bearer header set in the
// fetch chain — but for simple anchor downloads we ship the URL directly
// and rely on a server-side admin-only redirect with token query param).
//
// At v1 we surface the URL and let the SPA's apiFetch wrapper download it
// as a Blob, then trigger a save. That keeps the auth flow consistent.
export async function downloadInvoicePDF(
  authToken: string | null,
  slug: string,
  invoiceID: string,
): Promise<{ blob: Blob; filename: string }> {
  const headers: Record<string, string> = { Accept: 'application/pdf' };
  if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
  const res = await fetch(
    `${(await import('../config')).config.apiURL}/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/${encodeURIComponent(invoiceID)}/pdf`,
    { headers },
  );
  if (!res.ok) {
    const text = await res.text();
    let code = `http_${res.status}`;
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed.error === 'string') code = parsed.error;
    } catch {
      /* ignore */
    }
    throw new Error(code);
  }
  const blob = await res.blob();
  // Filename from Content-Disposition when present; fallback to invoice id.
  const cd = res.headers.get('content-disposition') ?? '';
  const match = /filename="?([^";]+)"?/i.exec(cd);
  const filename = match?.[1] ?? `invoice-${invoiceID}.pdf`;
  return { blob, filename };
}

export async function regenerateInvoicePDF(
  token: string | null,
  slug: string,
  invoiceID: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/${encodeURIComponent(invoiceID)}/pdf/regenerate`,
    { method: 'POST', token },
  );
}

export async function deleteDraftInvoice(token: string | null, slug: string, id: string): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoices/${encodeURIComponent(id)}`,
    { method: 'DELETE', token },
  );
}

// Invoice / fee types. Org-configurable; exactly one live type may be flagged
// is_membership_fee (paying an invoice of that type marks a member "membership
// paid" for the year) and one is_default (pre-selected in the editor).

export interface InvoiceType {
  id: string;
  name: string;
  is_membership_fee: boolean;
  is_default: boolean;
  sort_order: number;
  archived: boolean;
}

export interface InvoiceTypeInput {
  name: string;
  is_membership_fee: boolean;
  is_default: boolean;
  sort_order: number;
}

export async function listInvoiceTypes(
  token: string | null,
  slug: string,
  includeArchived = false,
): Promise<InvoiceType[]> {
  const suffix = includeArchived ? '?include_archived=true' : '';
  const res = await apiFetch<{ invoice_types: InvoiceType[] }>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoice-types${suffix}`,
    { token },
  );
  return res.invoice_types ?? [];
}

export async function createInvoiceType(
  token: string | null,
  slug: string,
  input: InvoiceTypeInput,
): Promise<InvoiceType> {
  return apiFetch<InvoiceType>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoice-types`,
    { method: 'POST', body: input, token },
  );
}

export async function updateInvoiceType(
  token: string | null,
  slug: string,
  id: string,
  input: InvoiceTypeInput,
): Promise<InvoiceType> {
  return apiFetch<InvoiceType>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoice-types/${encodeURIComponent(id)}`,
    { method: 'PUT', body: input, token },
  );
}

export async function deleteInvoiceType(token: string | null, slug: string, id: string): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoice-types/${encodeURIComponent(id)}`,
    { method: 'DELETE', token },
  );
}

export async function restoreInvoiceType(token: string | null, slug: string, id: string): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/billing/invoice-types/${encodeURIComponent(id)}/restore`,
    { method: 'POST', token },
  );
}
